import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Save, Upload, Trash2, Plus, ArrowLeft, Image as ImageIcon, 
  Users, Edit, Eye, X, UserPlus, Loader2, Play, Youtube
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssociationManagePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [associations, setAssociations] = useState([]);
  const [selectedAssoc, setSelectedAssoc] = useState(null);
  const [editData, setEditData] = useState({});
  const [presidents, setPresidents] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreatePresident, setShowCreatePresident] = useState(false);
  const [newPresident, setNewPresident] = useState({ email: '', password: '', name: '', association_id: '' });
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);
  const [archetypeOptions, setArchetypeOptions] = useState([]);
  const [gradientOptions, setGradientOptions] = useState([]);
  const [savingTheme, setSavingTheme] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoCropMode, setLogoCropMode] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const logoCanvasRef = React.useRef(null);
  const [heroImages, setHeroImages] = useState([]);
  const [savingHero, setSavingHero] = useState(false);

  const token = localStorage.getItem('cca_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAssociations = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/associations`);
      setAssociations(res.data);
    } catch (_) { /* ignore */ }
  }, []);

  const fetchPresidents = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/users/presidents`, { headers });
      setPresidents(res.data);
    } catch (_) { /* might not be admin */ }
  }, []);

  useEffect(() => {
    fetchAssociations();
    fetchPresidents();
    // Fetch archetype options
    axios.get(`${API_URL}/archetypes`).then(res => {
      setArchetypeOptions(res.data.archetypes || []);
      setGradientOptions(res.data.gradients || []);
    }).catch(() => {});
  }, [fetchAssociations, fetchPresidents]);

  const selectAssociation = (assoc) => {
    setSelectedAssoc(assoc);
    setEditData({
      description: assoc.description || '',
      mission: assoc.mission || '',
      cultural_highlights: assoc.cultural_highlights || '',
      cultural_background: assoc.cultural_background || '',
      values: (assoc.values || []).join(', '),
      archetype: assoc.archetype || '',
      theme_primary: assoc.theme?.primary || '',
      theme_secondary: assoc.theme?.secondary || '',
      theme_accent: assoc.theme?.accent || '',
      theme_bg: assoc.theme?.bg || '',
      theme_text: assoc.theme?.text || '',
      theme_gradient: assoc.theme?.gradient || 'none',
    });
    setHeroImages(assoc.hero_images || []);
  };

  const handleSaveTheme = async () => {
    if (!selectedAssoc) return;
    setSavingTheme(true);
    try {
      const payload = {};
      if (editData.archetype) payload.archetype = editData.archetype;
      const theme = { ...(selectedAssoc.theme || {}) };
      if (editData.theme_primary) theme.primary = editData.theme_primary;
      if (editData.theme_secondary) theme.secondary = editData.theme_secondary;
      if (editData.theme_accent) theme.accent = editData.theme_accent;
      if (editData.theme_bg) theme.bg = editData.theme_bg;
      if (editData.theme_text) theme.text = editData.theme_text;
      theme.gradient = editData.theme_gradient || 'none';
      payload.theme = theme;
      await axios.put(`${API_URL}/associations/${selectedAssoc.id}`, payload, { headers });
      toast.success('Template & Theme saved!');
      fetchAssociations();
      setSelectedAssoc(prev => ({ ...prev, ...payload, theme }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Theme update failed');
    } finally { setSavingTheme(false); }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoPreview(ev.target.result);
      setLogoCropMode(true);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoCrop = async (cropShape) => {
    if (!logoPreview || !selectedAssoc) return;
    setUploadingLogo(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = logoPreview; });
      const canvas = document.createElement('canvas');
      const size = Math.min(img.width, img.height);
      canvas.width = 400;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');
      // Crop to center square
      const sx = (img.width - size) / 2;
      const sy = (img.height - size) / 2;
      if (cropShape === 'circle') {
        ctx.beginPath();
        ctx.arc(200, 200, 200, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
      }
      ctx.drawImage(img, sx, sy, size, size, 0, 0, 400, 400);
      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
      const formData = new FormData();
      formData.append('file', blob, 'logo.png');
      const res = await axios.post(
        `${API_URL}/associations/${selectedAssoc.id}/logo/upload`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Logo uploaded!');
      setLogoCropMode(false);
      setLogoPreview(null);
      setLogoFile(null);
      fetchAssociations();
      setSelectedAssoc(prev => ({ ...prev, logo_url: res.data.logo_url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally { setUploadingLogo(false); }
  };

  const toggleHeroImage = (url) => {
    setHeroImages(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    );
  };

  const handleSaveHero = async () => {
    if (!selectedAssoc) return;
    setSavingHero(true);
    try {
      await axios.put(`${API_URL}/associations/${selectedAssoc.id}`, { hero_images: heroImages }, { headers });
      toast.success('Hero images saved!');
      fetchAssociations();
      setSelectedAssoc(prev => ({ ...prev, hero_images: heroImages }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hero update failed');
    } finally { setSavingHero(false); }
  };

  const handleSave = async () => {
    if (!selectedAssoc) return;
    setSaving(true);
    try {
      const payload = {
        description: editData.description,
        mission: editData.mission,
        cultural_highlights: editData.cultural_highlights,
        cultural_background: editData.cultural_background,
        values: editData.values.split(',').map(v => v.trim()).filter(Boolean),
      };
      await axios.put(`${API_URL}/associations/${selectedAssoc.id}`, payload, { headers });
      toast.success('Association updated!');
      fetchAssociations();
      // Update selected with new data
      setSelectedAssoc(prev => ({ ...prev, ...payload }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedAssoc) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('caption', '');
        await axios.post(
          `${API_URL}/associations/${selectedAssoc.id}/gallery/upload`,
          formData,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
      }
      toast.success(`${files.length} photo(s) uploaded!`);
      // Refresh association data
      const res = await axios.get(`${API_URL}/associations/by-slug/${selectedAssoc.slug}`);
      setSelectedAssoc(res.data);
      fetchAssociations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeletePhoto = async (index) => {
    if (!selectedAssoc) return;
    try {
      await axios.delete(`${API_URL}/associations/${selectedAssoc.id}/gallery/${index}`, { headers });
      toast.success('Photo removed');
      const res = await axios.get(`${API_URL}/associations/by-slug/${selectedAssoc.slug}`);
      setSelectedAssoc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleCreatePresident = async () => {
    try {
      const params = new URLSearchParams(newPresident).toString();
      await axios.post(`${API_URL}/admin/users/president?${params}`, {}, { headers });
      toast.success('President account created!');
      setShowCreatePresident(false);
      setNewPresident({ email: '', password: '', name: '', association_id: '' });
      fetchPresidents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Creation failed');
    }
  };

  const extractYoutubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const p of patterns) {
      const m = url.match(p);
      if (m) return m[1];
    }
    return null;
  };

  const handleAddVideo = async () => {
    if (!selectedAssoc || !newVideoUrl.trim()) return;
    const videoId = extractYoutubeId(newVideoUrl.trim());
    if (!videoId) { toast.error('Invalid YouTube URL or video ID'); return; }
    setAddingVideo(true);
    try {
      await axios.post(
        `${API_URL}/associations/${selectedAssoc.id}/videos`,
        { video_id: videoId, title: '', description: '' },
        { headers: { ...headers, 'Content-Type': 'application/json' } }
      );
      toast.success('Video added!');
      setNewVideoUrl('');
      const res = await axios.get(`${API_URL}/associations/by-slug/${selectedAssoc.slug}`);
      setSelectedAssoc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add video');
    } finally { setAddingVideo(false); }
  };

  const handleDeleteVideo = async (index) => {
    if (!selectedAssoc) return;
    try {
      await axios.delete(`${API_URL}/associations/${selectedAssoc.id}/videos/${index}`, { headers });
      toast.success('Video removed');
      const res = await axios.get(`${API_URL}/associations/by-slug/${selectedAssoc.slug}`);
      setSelectedAssoc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const isAdmin = user?.role === 'admin';
  const isPresident = user?.role === 'president';

  // President can only see their own association
  const visibleAssociations = isPresident
    ? associations.filter(a => a.id === user?.association_id)
    : associations;

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar variant="dark" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button onClick={() => navigate(-1)} className="text-white/50 hover:text-white text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-2xl font-bold text-white" data-testid="page-title">Association Content Portal</h1>
            <p className="text-white/50 text-sm mt-1">
              {isAdmin ? 'Manage all association pages — edit content, upload photos, create president accounts' : 'Edit your association page content and upload photos'}
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => setShowCreatePresident(!showCreatePresident)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-president-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Create President
            </Button>
          )}
        </div>

        {/* Create President Modal */}
        {showCreatePresident && (
          <Card className="mb-6 bg-slate-800 border-slate-700" data-testid="create-president-form">
            <CardHeader><CardTitle className="text-white text-lg">Create President Account</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input placeholder="Full Name" value={newPresident.name} onChange={e => setNewPresident(p => ({...p, name: e.target.value}))} className="bg-slate-700 border-slate-600 text-white" data-testid="president-name-input" />
                <Input placeholder="Email" type="email" value={newPresident.email} onChange={e => setNewPresident(p => ({...p, email: e.target.value}))} className="bg-slate-700 border-slate-600 text-white" data-testid="president-email-input" />
                <Input placeholder="Password" type="password" value={newPresident.password} onChange={e => setNewPresident(p => ({...p, password: e.target.value}))} className="bg-slate-700 border-slate-600 text-white" data-testid="president-password-input" />
                <select value={newPresident.association_id} onChange={e => setNewPresident(p => ({...p, association_id: e.target.value}))} className="bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm" data-testid="president-association-select">
                  <option value="">Select Association</option>
                  {associations.map(a => <option key={a.id} value={a.id}>{a.short_name} — {a.name}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreatePresident} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-president-btn">Create Account</Button>
                <Button variant="ghost" onClick={() => setShowCreatePresident(false)} className="text-white/60">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* President Accounts List */}
        {isAdmin && presidents.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm text-white/50 uppercase tracking-wider mb-3">Active President Accounts</h3>
            <div className="flex flex-wrap gap-2">
              {presidents.map(p => {
                const assoc = associations.find(a => a.id === p.association_id);
                return (
                  <Badge key={p.id} className="bg-slate-800 border-slate-700 text-white/70 px-3 py-1" data-testid={`president-badge-${p.id}`}>
                    <Users className="w-3 h-3 mr-1" /> {p.name} — {assoc?.short_name || 'Unassigned'}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Association List */}
          <div className="lg:col-span-4 space-y-2">
            <h3 className="text-sm text-white/50 uppercase tracking-wider mb-3">Select Association</h3>
            {visibleAssociations.map(assoc => (
              <button
                key={assoc.id}
                onClick={() => selectAssociation(assoc)}
                className={`w-full text-left p-3 rounded-lg border transition-colors flex items-center gap-3 ${
                  selectedAssoc?.id === assoc.id
                    ? 'bg-slate-700 border-emerald-500/50 text-white'
                    : 'bg-slate-800/50 border-slate-700 text-white/70 hover:bg-slate-800'
                }`}
                data-testid={`select-assoc-${assoc.slug}`}
              >
                <img src={assoc.logo_url || assoc.image_url} alt="" className="w-10 h-10 rounded-md object-cover flex-shrink-0" />
                <div className="min-w-0">
                  <h4 className="font-semibold text-sm truncate">{assoc.short_name}</h4>
                  <p className="text-xs text-white/40 truncate">{assoc.region} · {(assoc.gallery_images || []).length} photos</p>
                </div>
                {selectedAssoc?.id === assoc.id && <Edit className="w-4 h-4 ml-auto text-emerald-400 flex-shrink-0" />}
              </button>
            ))}
          </div>

          {/* Editor */}
          <div className="lg:col-span-8">
            {selectedAssoc ? (
              <div className="space-y-6">
                {/* Logo Upload */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="flex flex-row items-center justify-between pb-4">
                    <div>
                      <CardTitle className="text-white text-lg">Association Logo</CardTitle>
                      <p className="text-white/40 text-xs mt-1">Upload and crop logo for {selectedAssoc.short_name}</p>
                    </div>
                    <a href={`/${selectedAssoc.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-white/40 hover:text-white flex items-center gap-1" data-testid="preview-link">
                      <Eye className="w-3 h-3" /> Preview
                    </a>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start gap-6">
                      {/* Current logo */}
                      <div className="flex-shrink-0">
                        <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-slate-600 bg-slate-700" data-testid="current-logo">
                          {(selectedAssoc.logo_url || selectedAssoc.image_url) ? (
                            <img src={selectedAssoc.logo_url || selectedAssoc.image_url} alt="Logo" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/20 text-2xl font-bold">
                              {(selectedAssoc.short_name || '?')[0]}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Upload / Crop area */}
                      <div className="flex-1">
                        {logoCropMode && logoPreview ? (
                          <div className="space-y-3">
                            <p className="text-xs text-white/50 uppercase tracking-wider">Crop Preview (center-cropped to square)</p>
                            <div className="flex gap-3">
                              <div className="text-center">
                                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-emerald-500 mx-auto">
                                  <img src={logoPreview} alt="Preview" className="w-full h-full object-cover" />
                                </div>
                                <span className="text-[10px] text-white/40 mt-1 block">Circle</span>
                              </div>
                              <div className="text-center">
                                <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-slate-500 mx-auto">
                                  <img src={logoPreview} alt="Preview" className="w-full h-full object-cover" />
                                </div>
                                <span className="text-[10px] text-white/40 mt-1 block">Square</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button onClick={() => handleLogoCrop('circle')} disabled={uploadingLogo} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="crop-circle-btn">
                                {uploadingLogo ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save as Circle
                              </Button>
                              <Button onClick={() => handleLogoCrop('square')} disabled={uploadingLogo} size="sm" variant="outline" className="border-slate-600 text-white hover:bg-slate-700" data-testid="crop-square-btn">
                                Save as Square
                              </Button>
                              <Button onClick={() => { setLogoCropMode(false); setLogoPreview(null); }} size="sm" variant="ghost" className="text-white/50">
                                <X className="w-3 h-3 mr-1" /> Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-white text-sm transition-colors border border-slate-600" data-testid="upload-logo-btn">
                              <input type="file" accept="image/*" onChange={handleLogoSelect} className="hidden" />
                              <Upload className="w-4 h-4" /> Upload New Logo
                            </label>
                            <p className="text-xs text-white/30 mt-2">Recommended: square image, 400x400px or larger. Will be center-cropped.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Hero Images Picker */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-white text-lg">Hero Images</CardTitle>
                    <p className="text-white/40 text-xs mt-1">Select photos from gallery for the hero section. Supports single image or carousel.</p>
                  </CardHeader>
                  <CardContent>
                    {/* Currently selected hero images */}
                    {heroImages.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-white/50 uppercase tracking-wider mb-2">Selected ({heroImages.length})</p>
                        <div className="flex gap-2 flex-wrap">
                          {heroImages.map((url, i) => (
                            <div key={url} className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-emerald-500 group" data-testid={`hero-selected-${i}`}>
                              <img src={url} alt="" className="w-full h-full object-cover" />
                              <button
                                onClick={() => toggleHeroImage(url)}
                                className="absolute inset-0 bg-red-500/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4 text-white" />
                              </button>
                              <span className="absolute bottom-0.5 right-1 bg-black/60 text-white text-[9px] px-1 rounded">{i + 1}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Gallery photos to pick from */}
                    <p className="text-xs text-white/50 uppercase tracking-wider mb-2">Select from Gallery</p>
                    {selectedAssoc.gallery_images?.length > 0 ? (
                      <div className="grid grid-cols-5 gap-2 max-h-48 overflow-y-auto">
                        {selectedAssoc.gallery_images.map((img, i) => {
                          const url = img.url || img;
                          const isSelected = heroImages.includes(url);
                          return (
                            <button
                              key={i}
                              onClick={() => toggleHeroImage(url)}
                              className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 transition-all ${isSelected ? 'border-emerald-500 ring-2 ring-emerald-500/30' : 'border-slate-600 hover:border-slate-400'}`}
                              data-testid={`hero-pick-${i}`}
                            >
                              <img src={url} alt="" className="w-full h-full object-cover" />
                              {isSelected && (
                                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                                  <span className="text-white text-[10px] font-bold">{heroImages.indexOf(url) + 1}</span>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-white/30 text-sm italic py-4 text-center">No gallery photos yet. Upload photos in the Gallery section below first.</p>
                    )}

                    <Button onClick={handleSaveHero} disabled={savingHero} className="bg-emerald-600 hover:bg-emerald-700 text-white mt-4" data-testid="save-hero-btn">
                      {savingHero ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Hero Images</>}
                    </Button>
                  </CardContent>
                </Card>

                {/* Content Editor */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-white text-lg">Edit {selectedAssoc.short_name}</CardTitle>
                    <p className="text-white/40 text-sm mt-1">{selectedAssoc.name}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Description</label>
                      <textarea rows={3} value={editData.description} onChange={e => setEditData(d => ({...d, description: e.target.value}))}
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm resize-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                        data-testid="edit-description"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Mission</label>
                      <textarea rows={2} value={editData.mission} onChange={e => setEditData(d => ({...d, mission: e.target.value}))}
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm resize-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                        data-testid="edit-mission"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Cultural Highlights</label>
                      <textarea rows={2} value={editData.cultural_highlights} onChange={e => setEditData(d => ({...d, cultural_highlights: e.target.value}))}
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm resize-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                        data-testid="edit-cultural-highlights"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Values (comma-separated)</label>
                      <Input value={editData.values} onChange={e => setEditData(d => ({...d, values: e.target.value}))}
                        className="bg-slate-700 border-slate-600 text-white" placeholder="Unity, Heritage, Culture"
                        data-testid="edit-values"
                      />
                    </div>
                    <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-content-btn">
                      {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                    </Button>
                  </CardContent>
                </Card>

                {/* Template & Theme Manager */}
                {user?.role === 'admin' && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-white text-lg">Template & Theme</CardTitle>
                    <p className="text-white/40 text-xs mt-1">Change archetype layout and color scheme</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Archetype selector */}
                    <div>
                      <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Archetype Template</label>
                      <select
                        value={editData.archetype || ''}
                        onChange={e => setEditData(d => ({...d, archetype: e.target.value}))}
                        className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                        data-testid="select-archetype"
                      >
                        <option value="">Default (auto-assigned)</option>
                        {archetypeOptions.map(a => (
                          <option key={a.id} value={a.id}>{a.name} — {a.description}</option>
                        ))}
                      </select>
                    </div>

                    {/* Color Scheme */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Primary Color</label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={editData.theme_primary || '#005C46'}
                            onChange={e => setEditData(d => ({...d, theme_primary: e.target.value}))}
                            className="w-8 h-8 rounded cursor-pointer border-0"
                            data-testid="color-primary"
                          />
                          <Input value={editData.theme_primary || ''} onChange={e => setEditData(d => ({...d, theme_primary: e.target.value}))}
                            className="bg-slate-700 border-slate-600 text-white text-xs" placeholder="#005C46" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Secondary Color</label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={editData.theme_secondary || '#FFD700'}
                            onChange={e => setEditData(d => ({...d, theme_secondary: e.target.value}))}
                            className="w-8 h-8 rounded cursor-pointer border-0"
                            data-testid="color-secondary"
                          />
                          <Input value={editData.theme_secondary || ''} onChange={e => setEditData(d => ({...d, theme_secondary: e.target.value}))}
                            className="bg-slate-700 border-slate-600 text-white text-xs" placeholder="#FFD700" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Accent Color</label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={editData.theme_accent || '#B91C1C'}
                            onChange={e => setEditData(d => ({...d, theme_accent: e.target.value}))}
                            className="w-8 h-8 rounded cursor-pointer border-0"
                            data-testid="color-accent"
                          />
                          <Input value={editData.theme_accent || ''} onChange={e => setEditData(d => ({...d, theme_accent: e.target.value}))}
                            className="bg-slate-700 border-slate-600 text-white text-xs" placeholder="#B91C1C" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Background</label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={editData.theme_bg || '#0A1A14'}
                            onChange={e => setEditData(d => ({...d, theme_bg: e.target.value}))}
                            className="w-8 h-8 rounded cursor-pointer border-0"
                            data-testid="color-bg"
                          />
                          <Input value={editData.theme_bg || ''} onChange={e => setEditData(d => ({...d, theme_bg: e.target.value}))}
                            className="bg-slate-700 border-slate-600 text-white text-xs" placeholder="#0A1A14" />
                        </div>
                      </div>
                    </div>

                    {/* Text Color */}
                    <div>
                      <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Text Color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={editData.theme_text || '#FFFFFF'}
                          onChange={e => setEditData(d => ({...d, theme_text: e.target.value}))}
                          className="w-8 h-8 rounded cursor-pointer border-0"
                          data-testid="color-text"
                        />
                        <Input value={editData.theme_text || ''} onChange={e => setEditData(d => ({...d, theme_text: e.target.value}))}
                          className="bg-slate-700 border-slate-600 text-white text-xs flex-1" placeholder="#FFFFFF or #1A1A1A" />
                        <span className="text-xs text-white/30">Preview:</span>
                        <span className="text-sm font-bold px-2 py-1 rounded" style={{ color: editData.theme_text || '#FFFFFF', backgroundColor: editData.theme_bg || '#0A1A14' }}>Aa</span>
                      </div>
                    </div>

                    {/* Gradient Direction */}
                    <div>
                      <label className="text-xs text-white/50 uppercase tracking-wider block mb-1">Gradient Style</label>
                      <div className="grid grid-cols-5 gap-2">
                        {gradientOptions.map(g => (
                          <button key={g.id}
                            onClick={() => setEditData(d => ({...d, theme_gradient: g.id}))}
                            className={`p-2 rounded-md border text-xs text-center transition-all ${editData.theme_gradient === g.id ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-slate-600 text-white/50 hover:border-slate-500'}`}
                            data-testid={`gradient-${g.id}`}
                          >
                            <div className="w-full h-6 rounded mb-1" style={{
                              background: g.id === 'none'
                                ? (editData.theme_primary || '#005C46')
                                : g.id === 'horizontal' ? `linear-gradient(to right, ${editData.theme_primary || '#005C46'}, ${editData.theme_secondary || '#FFD700'})`
                                : g.id === 'vertical' ? `linear-gradient(to bottom, ${editData.theme_primary || '#005C46'}, ${editData.theme_secondary || '#FFD700'})`
                                : g.id === 'diagonal' ? `linear-gradient(135deg, ${editData.theme_primary || '#005C46'}, ${editData.theme_secondary || '#FFD700'})`
                                : `radial-gradient(circle, ${editData.theme_primary || '#005C46'}, ${editData.theme_secondary || '#FFD700'})`
                            }} />
                            {g.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Preview swatch */}
                    <div className="p-3 rounded-md border border-slate-600">
                      <p className="text-xs text-white/40 mb-2">Preview</p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded" style={{ backgroundColor: editData.theme_primary || '#005C46' }} title="Primary" />
                        <div className="w-8 h-8 rounded" style={{ backgroundColor: editData.theme_secondary || '#FFD700' }} title="Secondary" />
                        <div className="w-8 h-8 rounded" style={{ backgroundColor: editData.theme_accent || '#B91C1C' }} title="Accent" />
                        <div className="w-8 h-8 rounded border border-slate-500" style={{ backgroundColor: editData.theme_bg || '#0A1A14' }} title="Background" />
                        <div className="w-8 h-8 rounded border border-slate-500 flex items-center justify-center text-xs font-bold" style={{ backgroundColor: editData.theme_bg || '#0A1A14', color: editData.theme_text || '#FFFFFF' }} title="Text">Aa</div>
                        <div className="flex-1 h-8 rounded" style={{
                          background: editData.theme_gradient === 'none' || !editData.theme_gradient
                            ? (editData.theme_primary || '#005C46')
                            : editData.theme_gradient === 'horizontal' ? `linear-gradient(to right, ${editData.theme_primary || '#005C46'}, ${editData.theme_secondary || '#FFD700'})`
                            : editData.theme_gradient === 'vertical' ? `linear-gradient(to bottom, ${editData.theme_primary || '#005C46'}, ${editData.theme_secondary || '#FFD700'})`
                            : editData.theme_gradient === 'diagonal' ? `linear-gradient(135deg, ${editData.theme_primary || '#005C46'}, ${editData.theme_secondary || '#FFD700'})`
                            : `radial-gradient(circle, ${editData.theme_primary || '#005C46'}, ${editData.theme_secondary || '#FFD700'})`
                        }} title="Gradient" />
                      </div>
                    </div>

                    <Button onClick={handleSaveTheme} disabled={savingTheme} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-theme-btn">
                      {savingTheme ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Template & Theme</>}
                    </Button>
                  </CardContent>
                </Card>
                )}

                {/* Photo Gallery Manager */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="flex flex-row items-center justify-between pb-4">
                    <CardTitle className="text-white text-lg">Gallery Photos</CardTitle>
                    <label className="cursor-pointer" data-testid="upload-photos-btn">
                      <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors">
                        {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> Upload Photos</>}
                      </span>
                    </label>
                  </CardHeader>
                  <CardContent>
                    {(selectedAssoc.gallery_images || []).length > 0 ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                        {selectedAssoc.gallery_images.map((img, idx) => (
                          <div key={img.url || idx} className="relative group aspect-square rounded-md overflow-hidden" data-testid={`gallery-photo-${idx}`}>
                            <img src={img.url} alt={img.caption || ''} className="w-full h-full object-cover" />
                            <button
                              onClick={() => handleDeletePhoto(idx)}
                              className="absolute top-1 right-1 p-1 bg-red-600 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`delete-photo-${idx}`}
                            >
                              <Trash2 className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 border border-dashed border-slate-600 rounded-lg">
                        <ImageIcon className="w-10 h-10 text-white/15 mx-auto mb-3" />
                        <p className="text-white/30 text-sm">No photos yet. Upload cultural photos to showcase.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* YouTube Video Manager */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-white text-lg flex items-center gap-2"><Play className="w-5 h-5 text-red-500" /> YouTube Videos</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Add video input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="YouTube URL or Video ID (e.g. dQw4w9WgXcQ)"
                        value={newVideoUrl}
                        onChange={e => setNewVideoUrl(e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white flex-1"
                        data-testid="add-video-input"
                        onKeyDown={e => e.key === 'Enter' && handleAddVideo()}
                      />
                      <Button onClick={handleAddVideo} disabled={addingVideo || !newVideoUrl.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="add-video-btn">
                        {addingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
                      </Button>
                    </div>

                    {/* Video list */}
                    {(selectedAssoc.youtube_videos || []).length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {selectedAssoc.youtube_videos.map((vid, idx) => (
                          <div key={vid.video_id || idx} className="relative group rounded-md overflow-hidden border border-slate-600" data-testid={`video-card-${idx}`}>
                            <div className="aspect-video bg-slate-700">
                              <img
                                src={`https://img.youtube.com/vi/${vid.video_id}/mqdefault.jpg`}
                                alt={vid.title || `Video ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center shadow-lg"><Play className="w-5 h-5 text-white ml-0.5" /></div>
                              </div>
                            </div>
                            {vid.title && <p className="text-xs text-white/60 p-2 truncate">{vid.title}</p>}
                            <button
                              onClick={() => handleDeleteVideo(idx)}
                              className="absolute top-1 right-1 p-1 bg-red-600 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`delete-video-${idx}`}
                            >
                              <Trash2 className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border border-dashed border-slate-600 rounded-lg">
                        <Play className="w-8 h-8 text-white/15 mx-auto mb-2" />
                        <p className="text-white/30 text-sm">No videos yet. Add YouTube links to showcase cultural videos.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-center">
                <div>
                  <Edit className="w-10 h-10 text-white/15 mx-auto mb-3" />
                  <p className="text-white/40">Select an association to edit its content</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssociationManagePage;
